import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { EndPointGetRoutes, EndPointDeleteRoute, EndPointCreateUpdateRoute, EndPointGetRouteScheduleInfo } from '../Services/EndPointRoutes';
import { EndPointGetVehicles } from '../Services/EndPointVehicle';
import { EndPointGetPoints } from '../Services/EndPointAcopio';

export const RoutesContext = createContext();

// Custom hook to use the Vehicles context
export const useRoutes = () => {
  const context = useContext(RoutesContext);
  if (!context) {
    throw new Error('useRoutes must be used within a RoutesProvider');
  }
  return context;
};

// Provider component
export const RoutesProvider = ({ children }) => {
  const [miningSite, setMiningSite] = useState(0);
  const [openSave, setOpenSave] = useState(false);
  const [selected, setSelected] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [deleteItem, setDeleteItem] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scheduleInfo, setScheduleInfo] = useState(null);

  const fetchRoutes = async () => {
    setSelected(null);
    try {
      const response = await EndPointGetRoutes(miningSite);
      const routesData = response.data.data || [];
      setRoutes(routesData);
    } catch (error) {
      console.error('Error fetching routes:', error);
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await EndPointGetVehicles(miningSite);
      const vehiclesData = response.data.data || [];
      setVehicles(vehiclesData);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  };

  const fetchPoints = async () => {
    try {
      const response = await EndPointGetPoints(miningSite);
      const pointsData = response.data.data || [];
      setPoints(pointsData);
    } catch (error) {
      console.error('Error fetching points:', error);
    }
  };

  const fetchScheduleInfo = useCallback(async (routeId) => {
    if (!routeId || !miningSite) {
      setScheduleInfo(null);
      return;
    }
    try {
      const response = await EndPointGetRouteScheduleInfo(routeId, miningSite);
      if (response.data.success) {
        setScheduleInfo(response.data.data);
      } else {
        setScheduleInfo(null);
      }
    } catch (error) {
      console.error('Error fetching schedule info:', error);
      setScheduleInfo(null);
    }
  }, [miningSite]);

  const doDelete = async () => {
    setLoading(true);
    try {
      await EndPointDeleteRoute(deleteItem.routeId, miningSite);
      setSelected(null);
      setRoutes(routes.filter((item) => item.routeId !== deleteItem.routeId));
      setDeleteItem(null);
      setLoading(false);
    } catch (error) {
      console.error('Error deleting route:', error);
      setLoading(false);
    }
  };


  useEffect(() => {
    if (miningSite === 0) {
      setRoutes([]);
      setSelected(null);
      return;
    }

    fetchPoints();
    fetchVehicles();
    fetchRoutes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [miningSite]);

  const doSave = async (form) => {
    setLoading(true);
    try {
      const data = {
        ...form,
        miningSiteId: miningSite,
      };
      
      const response = await EndPointCreateUpdateRoute(data);

      if (!response.data.success) {
        setLoading(false);
        return { success: false, error: response.data.message };
      }
      
      await fetchRoutes();
      setOpenSave(true);
      setLoading(false);
      return { success: true };
    } catch (error) {
      console.error('Error saving route:', error);
      setLoading(false);
      return { success: false, error: 'Error saving route' };
    }
  };

  const contextValue = {
    miningSite,
    selected,
    routes,
    deleteItem,
    openSave,
    vehicles,
    points,
    loading,
    scheduleInfo,

    setOpenSave,
    setMiningSite,
    setSelected,
    setDeleteItem,
    setVehicles,
    setRoutes,
    setPoints,
    doSave,
    doDelete,
    fetchScheduleInfo,
  };

  return (
    <RoutesContext.Provider value={contextValue}>
      {children}
    </RoutesContext.Provider>
  );
};
